<template>
    <div class="print:hidden">
        <form action="{{ route('tasks.approve', taskId) }}" method="POST" @submit.prevent="handleSubmit">
            <div class="flex flex-col items-start gap-6 bg-primary p-5 lg:flex-row lg:items-stretch">
                <div class="flex flex-col justify-between text-white">
                    <h2 class="p-0 text-xl font-bold">{{ trans('tasks.supervisor.approval.title') }}</h2>
                    <p class="!mb-0">{{ trans('tasks.supervisor.approval.description') }}</p>
                </div>
                <div class="flex w-full items-end gap-6">
                    <div class="flex-grow">
                        <jet-select
                            v-model="approvedById"
                            :disabled="disabled"
                            :enable-search="true"
                            :horizontal="false"
                            :label="trans('tasks.supervisor.approval.label')"
                            :options="approvers"
                            :required="false"
                            :resettable="false"
                            form-group-classes="mb-0"
                            label-classes="text-white"
                            name="approved_by_id"
                        />
                    </div>
                    <div>
                        <jet-button
                            :disabled="!approvedById"
                            :is-processing="processing"
                            class="!h-12 min-w-30 cursor-pointer !rounded-xl !border-green !bg-green px-4 font-bold text-white hover:brightness-90"
                        >
                            {{ submitLabel }}
                        </jet-button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>
<script setup>
import { trans } from '@/mixins/i18n.js';
import JetSelect from '@/Jetstream/Select.vue';
import JetButton from '@/Jetstream/Button.vue';
import { computed, ref } from 'vue';
import axios from 'axios';
import { eventBus, events } from '@/eventBus.js';

const props = defineProps({
    taskId: String,
    approvers: Array,
    approverId: String,
    disabled: { type: Boolean, default: false },
    submitLabel: {
        type: String,
        default: () => trans('general.action.approve'),
    },
});

const emit = defineEmits(['approved']);

const processing = ref(false);
const approvedById = ref(props.approverId);

const handleSubmit = async () => {
    if (processing.value || !approvedById.value) {
        return;
    }

    processing.value = true;

    try {
        const { data } = await axios.post(route('tasks.approve', props.taskId), {
            approved_by_id: approvedById.value,
        });

        if (data.flashMessage) {
            eventBus.$emit(events.presentFlashMessage, { payload: data.flashMessage });
        }

        emit('approved', data);
    } catch (error) {
        const flashMessage = error.response?.data?.flashMessage ?? {
            message: error.message,
            status: 'error',
        };
        eventBus.$emit(events.presentFlashMessage, { payload: flashMessage });
    } finally {
        processing.value = false;
    }
};

const approvers = computed(() => {
    return props.approvers.map((approver) => ({
        label: approver.name,
        value: approver.id,
    }));
});
</script>
<style scoped>
:deep(.form-label) {
    font-size: 16px !important;
    line-height: 2;
    margin: 0;
}
</style>
